import axios from 'axios';
import oidc from '../vue-oidc-client';
import { resolveAppErrorKind, showAppError } from './appError';

const BASE_URL = process.env.VUE_APP_API_URL;

function createAxiosInstance(baseURL = BASE_URL) {
    const instance = axios.create({
        baseURL,
    });

    // instance.defaults.headers.common['Accept'] = 'application/json';
    instance.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

    return instance;
}

function useJwtAuthorization(instance) {
    instance.interceptors.request.use(
        async (request) => {
            // Пока идёт тихое продление, токена нет — дожидаемся, чтобы не отправить
            // запрос с пустым Bearer и не словить лишний 401.
            if (!oidc.accessToken && oidc.renewPromise) {
                await oidc.renewPromise;
            }

            request.headers.authorization = 'Bearer ' + oidc.accessToken;

            return request;
        },
        (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(undefined, (error) => {
        if (!error || !error.response || error.response.status !== 401) {
            return Promise.reject(error);
        }

        // Токен мог протухнуть прямо перед отправкой запроса. Сначала пробуем поднять
        // сессию тихо и повторить — ровно один раз: если API отвечает 401 и со свежим
        // токеном, дело не в токене, и второй круг ничего не изменит.
        //
        // Параллельные 401 не плодят попыток: oidc.signInSilent() отдаёт им общий промис.
        if (error.config && !error.config.isAuthRetried) {
            error.config.isAuthRetried = true;

            return oidc.signInSilent().then(
                () => instance.request(error.config),
                () => redirectToSso(error),
            );
        }

        return redirectToSso(error);
    });

    return instance;
}

/**
 * Тихо войти не удалось — остаётся полный редирект на SSO.
 *
 * Путь сохраняет сам oidc: он берёт текущий адрес и вернёт на него после входа. Экран
 * ошибки на неудачном редиректе (упёрлись в лимит или SSO не ответил) тоже поднимает он —
 * здесь достаточно не дать отказу всплыть необработанным.
 *
 * @param {object} error Исходная ошибка 401, с которой запрос и завершится.
 * @returns {Promise<never>}
 */
function redirectToSso(error) {
    // Флаг ставим до вызова, а не в then: роутер читает его в обработчике этой же ошибки,
    // и он не должен зависеть от того, успеет ли промис отработать до выгрузки страницы.
    error.isAuthRedirectStarted = true;

    return oidc
        .signInByRedirect()
        .catch(() => {})
        .then(() => Promise.reject(error));
}

/**
 * Фатальные ответы (нет связи, 5xx) поднимают оверлей поверх текущего экрана.
 *
 * Отдельно от useJwtAuthorization: 401 сюда не попадает намеренно — им занимается
 * авторизационный перехватчик, который сначала пробует тихо продлить токен и повторить
 * запрос. Оверлей на каждый 401 накрывал бы штатное продление.
 *
 * @param {import('axios').AxiosInstance} instance
 * @returns {import('axios').AxiosInstance}
 */
function useAppErrorOverlay(instance) {
    instance.interceptors.response.use(undefined, (error) => {
        const status = error && error.response && error.response.status;

        if (status !== 401) {
            const kind = resolveAppErrorKind(error);

            if (kind) {
                showAppError(kind);
            }
        }

        return Promise.reject(error);
    });

    return instance;
}

function createApiInstance(BASE_URL) {
    const client = createAxiosInstance(BASE_URL);

    useJwtAuthorization(client);
    // Порядок важен: перехватчики ответов выполняются в порядке регистрации, и оверлей
    // должен смотреть на ошибку ПОСЛЕ того, как авторизационный перехватчик отработал свою
    // попытку продлить токен и повторить запрос.
    useAppErrorOverlay(client);

    return client;
}

export { createApiInstance, createAxiosInstance, useJwtAuthorization, useAppErrorOverlay };
export default createApiInstance(BASE_URL);
